// src/screens/AccountPendingDeletionScreen.js
// ============================================================================
// Shown right after a user requests account deletion (DeleteAccountScreen
// signs them out and replaces to here) and when a sign-in fails with
// auth/user-disabled (LoginScreen routes here). The viewer is LOGGED OUT —
// their Auth account is disabled — so restore is support-mediated: the
// restoreAccount callable is admin-only, and the deletion-confirmation email
// tells them to contact us within the review period. This screen's job is to
// confirm what happened, set expectations for the ~7–14 day review window,
// and give a clear "changed your mind / wasn't me" path to support.
// ============================================================================

import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Footer from '../components/Footer';

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#101010',
  BORDER: '#1c1c1c',
  GOLD: '#FCD000',
  CYAN: '#3aafff',
  RED: '#E74C3C',
  TEXT: '#EDEDED',
  DIM: '#9aa0b4',
};

const STEPS = [
  {
    icon: 'lock-closed-outline',
    title: 'Your account is disabled',
    body: "You can't sign in while the deletion request is being processed.",
  },
  {
    icon: 'time-outline',
    title: 'Review period: about 7–14 days',
    body: 'Our team reviews every request. Earnings not yet paid out when your account is deleted may not be recoverable.',
  },
  {
    icon: 'trash-outline',
    title: 'Then your data is deleted',
    body: 'After the review period your account and personal data are permanently deleted, except records we keep as described in the Privacy Policy. This cannot be undone.',
  },
];

export default function AccountPendingDeletionScreen({ navigation }) {
  return (
    <View style={styles.root}>
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.card}>
          <View style={styles.iconWrap}>
            <Ionicons name="hourglass-outline" size={34} color={COLORS.GOLD} />
          </View>
          <Text style={styles.title}>Deletion Request Received</Text>
          <Text style={styles.subtitle}>
            Your CritCash account is now pending deletion. A confirmation email
            is on its way to your account email.
          </Text>

          <View style={styles.steps}>
            {STEPS.map((s, i) => (
              <View key={i} style={styles.stepRow}>
                <Ionicons name={s.icon} size={20} color={COLORS.CYAN} style={styles.stepIcon} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.stepTitle}>{s.title}</Text>
                  <Text style={styles.stepBody}>{s.body}</Text>
                </View>
              </View>
            ))}
          </View>

          <View style={styles.restoreBox}>
            <Text style={styles.restoreTitle}>Changed your mind — or wasn't you?</Text>
            <Text style={styles.restoreBody}>
              Email us within the review period and we can restore your account
              exactly as it was. If you didn't request this deletion, contact us
              immediately.
            </Text>
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => Linking.openURL('mailto:support@critcash.com?subject=Restore%20my%20CritCash%20account')}
            >
              <Ionicons name="mail-outline" size={16} color="#000" style={{ marginRight: 8 }} />
              <Text style={styles.primaryBtnText}>Email support@critcash.com</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => navigation.navigate('HelpSupport')}>
              <Text style={styles.helpLink}>Visit Help & Support</Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity
            style={styles.secondaryBtn}
            onPress={() => navigation.replace('Landing')}
          >
            <Text style={styles.secondaryBtnText}>Return to Home</Text>
          </TouchableOpacity>
        </View>

        <Footer navigation={navigation} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: COLORS.BG },
  scroll: { flexGrow: 1, paddingHorizontal: 20, paddingTop: 48, paddingBottom: 20 },

  card: {
    maxWidth: 560,
    width: '100%',
    alignSelf: 'center',
    backgroundColor: COLORS.CARD,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 14,
    padding: 26,
    alignItems: 'center',
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 24px rgba(0,0,0,0.4)' } : {}),
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(252,208,0,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(252,208,0,0.3)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: {
    color: COLORS.TEXT,
    fontSize: 24,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 10,
  },
  subtitle: {
    color: COLORS.DIM,
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    marginBottom: 22,
  },

  steps: { width: '100%', gap: 14, marginBottom: 22 },
  stepRow: { flexDirection: 'row', alignItems: 'flex-start' },
  stepIcon: { marginRight: 12, marginTop: 2 },
  stepTitle: { color: COLORS.TEXT, fontSize: 14, fontWeight: '700', marginBottom: 2 },
  stepBody: { color: COLORS.DIM, fontSize: 13, lineHeight: 19 },

  restoreBox: {
    width: '100%',
    backgroundColor: '#161616',
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 10,
    padding: 16,
    alignItems: 'center',
    marginBottom: 16,
  },
  restoreTitle: { color: COLORS.GOLD, fontSize: 14, fontWeight: '800', marginBottom: 6, textAlign: 'center' },
  restoreBody: { color: COLORS.DIM, fontSize: 13, lineHeight: 19, textAlign: 'center', marginBottom: 14 },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.GOLD,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  primaryBtnText: { color: '#000', fontWeight: '800', fontSize: 14 },
  helpLink: { color: COLORS.CYAN, fontSize: 13, textDecorationLine: 'underline', marginTop: 12 },

  secondaryBtn: {
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 28,
  },
  secondaryBtnText: { color: COLORS.TEXT, fontWeight: '700', fontSize: 14 },
});
